/**
 * Empire-PWA chassis · crypto.
 *
 * Two KDF paths:
 *   v1 = PBKDF2-SHA256, 600K iters  (legacy — current `data.encrypted.json` blob)
 *   v2 = Argon2id, m=64 MiB, t=3, p=4 (new — post-AAA, all writes go through this)
 *
 * Ciphertext envelope (base64-encoded bytes):
 *   v1: [12B IV][N bytes AES-GCM ciphertext]                    -- legacy bare
 *   v2: [1B version=0x02][16B salt][12B IV][N bytes AES-GCM]    -- self-describing
 *
 * On read: probe leading byte. If 0x02, derive via Argon2id with embedded salt.
 *          Otherwise fallback to v1 PBKDF2 + the canonical app salt.
 *
 * All data leaving OPFS is v2. The fixed `data.encrypted.json` from the build
 * pipeline remains v1 until re-encrypted on first unlock (transparent upgrade).
 */
import { argon2id } from 'hash-wasm';
import type { CiphertextB64, KdfVersion, Passcode } from './branded.ts';

const PBKDF2_ITERS = 600_000;
const LEGACY_SALT = new TextEncoder().encode('dial-v1-salt'); // empire dial v1 — different from almari intentionally
const V2_TAG = 0x02;
const V2_SALT_LEN = 16;
const IV_LEN = 12;

const ARGON2_PARAMS = {
  parallelism: 4,
  iterations: 3,
  memorySize: 65_536, // 64 MiB
  hashLength: 32,
  outputType: 'binary' as const,
};

async function deriveKeyV1(passcode: Passcode): Promise<CryptoKey> {
  const passKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passcode),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: LEGACY_SALT, iterations: PBKDF2_ITERS, hash: 'SHA-256' },
    passKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function deriveKeyV2(passcode: Passcode, salt: Uint8Array): Promise<CryptoKey> {
  const raw = await argon2id({
    password: passcode,
    salt,
    ...ARGON2_PARAMS,
  });
  const keyBuf = new Uint8Array(raw.length);
  keyBuf.set(raw);
  return crypto.subtle.importKey(
    'raw',
    keyBuf.buffer,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function b64encode(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}
function b64decode(s: string): Uint8Array {
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}

export async function encryptJSON(passcode: Passcode, data: unknown, version: KdfVersion = 2): Promise<CiphertextB64> {
  const plaintext = new TextEncoder().encode(JSON.stringify(data));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));

  if (version === 1) {
    const key = await deriveKeyV1(passcode);
    const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext));
    const out = new Uint8Array(IV_LEN + ct.byteLength);
    out.set(iv, 0);
    out.set(ct, IV_LEN);
    return b64encode(out) as CiphertextB64;
  }

  const salt = crypto.getRandomValues(new Uint8Array(V2_SALT_LEN));
  const key = await deriveKeyV2(passcode, salt);
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext));
  const out = new Uint8Array(1 + V2_SALT_LEN + IV_LEN + ct.byteLength);
  out[0] = V2_TAG;
  out.set(salt, 1);
  out.set(iv, 1 + V2_SALT_LEN);
  out.set(ct, 1 + V2_SALT_LEN + IV_LEN);
  return b64encode(out) as CiphertextB64;
}

export async function decryptJSON<T>(passcode: Passcode, blob: CiphertextB64): Promise<T> {
  const bytes = b64decode(blob);
  if (bytes.length === 0) throw new Error('empty ciphertext');

  if (bytes[0] === V2_TAG && bytes.length > 1 + V2_SALT_LEN + IV_LEN) {
    const salt = bytes.slice(1, 1 + V2_SALT_LEN);
    const iv = bytes.slice(1 + V2_SALT_LEN, 1 + V2_SALT_LEN + IV_LEN);
    const ct = bytes.slice(1 + V2_SALT_LEN + IV_LEN);
    const key = await deriveKeyV2(passcode, salt);
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
    return JSON.parse(new TextDecoder().decode(pt)) as T;
  }

  const iv = bytes.slice(0, IV_LEN);
  const ct = bytes.slice(IV_LEN);
  const key = await deriveKeyV1(passcode);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return JSON.parse(new TextDecoder().decode(pt)) as T;
}

export function ciphertextVersion(blob: CiphertextB64): KdfVersion {
  const bytes = b64decode(blob);
  return bytes[0] === V2_TAG ? 2 : 1;
}

export async function rotateToV2<T>(passcode: Passcode, oldBlob: CiphertextB64): Promise<CiphertextB64> {
  const data = await decryptJSON<T>(passcode, oldBlob);
  return encryptJSON(passcode, data, 2);
}

export async function sha256Hex(s: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
